import React, { useEffect, useState } from 'react';
import { reportApi, userApi } from '../api';
import { Btn, Alert, Spinner } from '../components/ui';

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a   = document.createElement('a');
  a.href    = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Stat pill ─────────────────────────────────────────────────────────────────
function StatPill({ label, value, color }) {
  return (
    <div style={{ textAlign: 'center', padding: '14px 10px', background: 'var(--color-surface)', borderRadius: 'var(--radius-md)', border: `1px solid var(--color-border)`, borderTop: `3px solid ${color}` }}>
      <p style={{ fontSize: '24px', fontWeight: 700, color }}>{value ?? 0}</p>
      <p style={{ fontSize: '11px', color: 'var(--color-text-muted)', marginTop: '4px', fontWeight: 500 }}>{label}</p>
    </div>
  );
}

// ── Employee report tab ────────────────────────────────────────────────────────
function EmployeeReportTab({ employees }) {
  const [selectedId, setSelected] = useState('');
  const [from,       setFrom]     = useState('');
  const [to,         setTo]       = useState('');
  const [report,     setReport]   = useState(null);
  const [loading,    setLoading]  = useState(false);
  const [exporting,  setExporting]= useState('');
  const [error,      setError]    = useState('');

  const run = async () => {
    if (!selectedId) { setError('Select an employee'); return; }
    setError(''); setLoading(true); setReport(null);
    try {
      const res = await reportApi.getEmployeeReport(selectedId, from || undefined, to || undefined);
      setReport(res.data.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to generate report');
    } finally { setLoading(false); }
  };

  const exportAs = async (format) => {
    if (!selectedId) return;
    setExporting(format);
    try {
      const res = await reportApi.exportEmployeeReport(selectedId, format, from || undefined, to || undefined);
      downloadBlob(res.data, `employee-report.${format === 'pdf' ? 'pdf' : 'xlsx'}`);
    } catch (e) {
      setError('Export failed');
    } finally { setExporting(''); }
  };

  return (
    <div>
      <p style={S.sectionTitle}>Per-Employee Summary</p>
      {error && <Alert type="error" message={error} />}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', marginTop: '12px', marginBottom: '16px' }}>
        <div>
          <label style={S.label}>Employee *</label>
          <select value={selectedId} onChange={e => setSelected(e.target.value)} style={{ ...S.input, marginTop: '6px' }}>
            <option value="">— Select employee —</option>
            {employees.map(e => <option key={e.id} value={e.id}>{e.name} ({e.employeeId})</option>)}
          </select>
        </div>
        <div>
          <label style={S.label}>From date</label>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ ...S.input, marginTop: '6px' }} />
        </div>
        <div>
          <label style={S.label}>To date</label>
          <input type="date" value={to}   onChange={e => setTo(e.target.value)}   style={{ ...S.input, marginTop: '6px' }} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
        <Btn onClick={run} loading={loading}>Generate Report</Btn>
        {report && (
          <>
            <Btn variant="secondary" onClick={() => exportAs('excel')} loading={exporting === 'excel'}>Export Excel</Btn>
            <Btn variant="secondary" onClick={() => exportAs('pdf')}   loading={exporting === 'pdf'}>Export PDF</Btn>
          </>
        )}
      </div>

      {loading && <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}><Spinner /></div>}

      {report && (
        <div>
          <div style={{ padding: '16px', background: 'var(--color-primary-lighter)', borderRadius: 'var(--radius-md)', marginBottom: '16px' }}>
            <p style={{ fontWeight: 600, color: 'var(--color-primary)', fontSize: '16px' }}>{report.employee?.name}</p>
            <p style={{ fontSize: '13px', color: 'var(--color-primary)', opacity: 0.8 }}>
              {report.employee?.employeeId} · {report.dateRange?.from} to {report.dateRange?.to}
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '8px', marginBottom: '20px' }}>
            <StatPill label="Assigned"          value={report.assigned}        color="var(--color-primary)" />
            <StatPill label="Done On Time"       value={report.completedOnTime} color="var(--color-success)" />
            <StatPill label="Done Late"          value={report.completedLate}   color="var(--color-warning)" />
            <StatPill label="Overdue"            value={report.overdue}         color="var(--color-danger)"  />
            <StatPill label="In Progress"        value={report.inProgress}      color="var(--color-accent)"  />
            <StatPill label="On Hold"            value={report.onHold}          color="#D69E2E"              />
            <StatPill label="Pending"            value={report.pending}         color="var(--color-text-muted)" />
          </div>

          {report.tasks?.length > 0 && (
            <div>
              <p style={S.sectionTitle}>Task Breakdown ({report.tasks.length})</p>
              <div style={{ overflowX: 'auto', marginTop: '8px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: 'var(--color-primary)', color: '#fff' }}>
                      {['Title','Priority','Status','Deadline','Completed','Late','Department'].map(h => (
                        <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: '12px' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {report.tasks.map((task, i) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? 'var(--color-bg)' : 'var(--color-surface)' }}>
                        <td style={{ padding: '8px 12px', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.title}</td>
                        <td style={{ padding: '8px 12px' }}>{task.priority}</td>
                        <td style={{ padding: '8px 12px' }}>{task.status?.replace('_',' ')}</td>
                        <td style={{ padding: '8px 12px' }}>{task.deadline}</td>
                        <td style={{ padding: '8px 12px' }}>{task.completedAt}</td>
                        <td style={{ padding: '8px 12px', color: task.wasLate === 'true' ? 'var(--color-danger)' : 'var(--color-success)' }}>
                          {task.wasLate === 'true' ? 'Yes' : task.completedAt ? 'No' : '—'}
                        </td>
                        <td style={{ padding: '8px 12px' }}>{task.department}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Overdue report tab ─────────────────────────────────────────────────────────
function OverdueReportTab() {
  const [from,      setFrom]      = useState('');
  const [to,        setTo]        = useState('');
  const [report,    setReport]    = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [exporting, setExporting] = useState('');
  const [error,     setError]     = useState('');

  const run = async () => {
    setError(''); setLoading(true); setReport(null);
    try {
      const res = await reportApi.getOverdueReport(from || undefined, to || undefined);
      setReport(res.data.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to generate report');
    } finally { setLoading(false); }
  };

  const exportAs = async (format) => {
    setExporting(format);
    try {
      const res = await reportApi.exportOverdueReport(format, from || undefined, to || undefined);
      downloadBlob(res.data, `overdue-report.${format === 'pdf' ? 'pdf' : 'xlsx'}`);
    } catch (e) {
      setError('Export failed');
    } finally { setExporting(''); }
  };

  return (
    <div>
      <p style={S.sectionTitle}>Overdue Tasks Report</p>
      {error && <Alert type="error" message={error} />}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', marginTop: '12px', marginBottom: '16px' }}>
        <div>
          <label style={S.label}>Deadline from</label>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ ...S.input, marginTop: '6px' }} />
        </div>
        <div>
          <label style={S.label}>Deadline to</label>
          <input type="date" value={to}   onChange={e => setTo(e.target.value)}   style={{ ...S.input, marginTop: '6px' }} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
        <Btn onClick={run} loading={loading}>Generate Report</Btn>
        {report && (
          <>
            <Btn variant="secondary" onClick={() => exportAs('excel')} loading={exporting === 'excel'}>Export Excel</Btn>
            <Btn variant="secondary" onClick={() => exportAs('pdf')}   loading={exporting === 'pdf'}>Export PDF</Btn>
          </>
        )}
      </div>

      {loading && <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}><Spinner /></div>}

      {report && (
        <div>
          <div style={{ padding: '12px 16px', background: '#FFF5F5', border: '1px solid #FEB2B2', borderRadius: 'var(--radius-md)', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <p style={{ fontWeight: 600, color: 'var(--color-danger)', fontSize: '16px' }}>
              {report.total} overdue task{report.total !== 1 ? 's' : ''}
            </p>
            <p style={{ fontSize: '13px', color: 'var(--color-danger)', opacity: 0.8 }}>
              {report.dateRange?.from} to {report.dateRange?.to}
            </p>
          </div>

          {report.tasks?.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: 'var(--color-danger)', color: '#fff' }}>
                    {['Title','Priority','Deadline','Days Overdue','Assigned To','Department'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: '12px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {report.tasks.map((task, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#FFF5F5' : 'var(--color-surface)' }}>
                      <td style={{ padding: '8px 12px', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.title}</td>
                      <td style={{ padding: '8px 12px' }}>{task.priority}</td>
                      <td style={{ padding: '8px 12px' }}>{task.deadline}</td>
                      <td style={{ padding: '8px 12px', color: 'var(--color-danger)', fontWeight: 600 }}>{task.daysOverdue}d</td>
                      <td style={{ padding: '8px 12px' }}>{task.assignedTo}</td>
                      <td style={{ padding: '8px 12px' }}>{task.department}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Reports page ─────────────────────────────────────────────────────────
export default function Reports() {
  const [employees, setEmployees] = useState([]);
  const [activeTab, setActiveTab] = useState('employee');

  useEffect(() => {
    userApi.getAll()
      .then(r => setEmployees((r.data.data || []).filter(e => e.active)))
      .catch(() => {});
  }, []);

  const TABS = [
    { key: 'employee', label: 'Per-Employee' },
    { key: 'overdue',  label: 'Overdue Tasks' },
  ];

  return (
    <div style={{ padding: '24px', maxWidth: '900px' }}>
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: 600, color: 'var(--color-primary)', marginBottom: '4px' }}>Reports</h1>
        <p style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>
          Generate and export reports as PDF or Excel.
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--color-border)', marginBottom: '20px' }}>
        {TABS.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
            padding: '10px 20px', background: 'none', border: 'none', cursor: 'pointer',
            fontSize: '14px', fontWeight: activeTab === tab.key ? 600 : 400,
            color: activeTab === tab.key ? 'var(--color-primary)' : 'var(--color-text-muted)',
            borderBottom: `2px solid ${activeTab === tab.key ? 'var(--color-primary)' : 'transparent'}`,
          }}>{tab.label}</button>
        ))}
      </div>

      {activeTab === 'employee' && <EmployeeReportTab employees={employees} />}
      {activeTab === 'overdue'  && <OverdueReportTab />}
    </div>
  );
}

const S = {
  sectionTitle: { fontSize: '14px', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: '4px' },
  label:        { fontSize: '12px', fontWeight: 500, color: 'var(--color-text-secondary)' },
  input:        { width: '100%', padding: '9px 12px', borderRadius: 'var(--radius-md)', border: '1.5px solid var(--color-border)', fontSize: '14px', color: 'var(--color-text)', background: 'var(--color-surface)' },
};
